/** Meldinger helpers: chat-style timestamps, message grouping and the per-thread draft store. */
const DAY = 86400000;
const WEEKDAY = ["søn.", "man.", "tir.", "ons.", "tor.", "fre.", "lør."];

/** Messenger-style time: «2 min», «11:42», «I går», «tor.», «12. mai». */
export function when(iso, now = Date.now()) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = now - d.getTime();
  if (diff < 60000) return "nå";
  if (diff < 3600000) return `${Math.round(diff / 60000)} min`;
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  if (d >= today) return d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
  if (d >= new Date(today.getTime() - DAY)) return "I går";
  if (d >= new Date(today.getTime() - 6 * DAY)) return WEEKDAY[d.getDay()];
  return d.toLocaleDateString("nb-NO", { day: "numeric", month: "short" }).replace(/\.$/, "");
}

export const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" }) : "");

/** Day separator label: «I dag», «I går», «torsdag 12. juni». */
export function dayLabel(iso, now = Date.now()) {
  const d = new Date(iso);
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  if (d >= today) return "I dag";
  if (d >= new Date(today.getTime() - DAY)) return "I går";
  return d.toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" });
}

const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

/** Group consecutive messages from the same author within five minutes; insert day markers. */
export function groupMessages(messages = []) {
  const out = [];
  let group = null;
  messages.forEach((m) => {
    const prev = out.length ? out[out.length - 1] : null;
    if (!prev || prev.type !== "group" || !sameDay(prev.at, m.created_at)) {
      if (!prev || prev.type !== "day" || !sameDay(prev.at, m.created_at)) out.push({ type: "day", at: m.created_at, key: `day-${m.created_at}` });
      group = null;
    }
    const continues = group && group.author?.id === m.author?.id && m.kind === "melding" && group.kind === "melding" && !m.reply_to && new Date(m.created_at) - new Date(group.last) < 5 * 60000;
    if (continues) { group.items.push(m); group.last = m.created_at; }
    else { group = { type: "group", key: m.id, author: m.author, kind: m.kind, at: m.created_at, last: m.created_at, items: [m] }; out.push(group); }
  });
  return out;
}

export const firstName = (name) => (!name ? "" : /^\d/.test(name) ? name : name.split(" ")[0]);

/** Draft per thread (text, attachments, shares, reply) – survives tab changes, never crosses into another thread. */
const KEY = (tid) => `sk.thread.draft.${tid || "new"}`;
export const readDraft = (tid) => { try { return JSON.parse(sessionStorage.getItem(KEY(tid)) || "null") || null; } catch { return null; } };
export const writeDraft = (tid, d) => { if (d && (d.text || d.attachments?.length || d.shares?.length || d.reply)) sessionStorage.setItem(KEY(tid), JSON.stringify(d)); else sessionStorage.removeItem(KEY(tid)); };

export const fmtSize = (n) => (!n ? "" : n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} kB` : `${(n / 1048576).toFixed(1).replace(".", ",")} MB`);

export const participantsLine = (people = [], me, max = 3) => {
  const names = people.map((p) => (p.id === me ? "deg" : firstName(p.name))).filter(Boolean);
  if (!names.length) return "";
  if (names.length <= max) return names.join(", ");
  return `${names.slice(0, max).join(", ")} +${names.length - max}`;
};
